import React, { useMemo, useState } from 'react';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import set from 'lodash/set';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import debounce from 'lodash/debounce';
import startCase from 'lodash/startCase';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { FormApi, FormFooter, FormHeader, FormSection, RequiredAsterisk, Field, Label } from '@atlaskit/form';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import TextField from '@atlaskit/textfield';
import Select from '@atlaskit/select';
import { Box, Text, Flex } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import { P200 } from '@atlaskit/theme/colors';
import AddIcon from '@atlaskit/icon/glyph/add';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import {
  AddressFormSectionV2,
  ErrorComponents,
  InflightInvoiceBanner,
  InfoPanel,
  NotificationTypes,
  getFallbackTaxId,
  getTaxIds,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  errorNotificationBuilder,
  useFeatureToggle,
  formatAddressLabel,
  AssociatedBillingProfiles,
  IsoSelectItem,
  Divider,
} from '@css-xp/base';
import {
  CommerceAddress,
  FetchShipToInPpcQuery,
  FetchShipToInPpcQueryVariables,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  ENABLE_DESIGN_ITERATION_1,
  CCP_SEARCH_MAX_LIMIT,
  STATSIG_FEATURE_GATES,
  UPDATE_FAILED_MESSAGE,
} from '@css-xp/ppc-constants';
import {
  failureMessage,
  DEFAULT_INFLIGHT_BANNER_MESSAGE,
  HelpContent,
  getCurrencyByCountry,
  Currency,
  getSupportUrl,
} from '@css-xp/ppc-common';
import { getPartnerSupportUrl } from '@css-xp/ppc-navigation-partner-support-utils';
import { updateShipToParty, updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateBillToAndShipToParties } from '@atlassian/cssxp-types-billing';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchShipToInPPC } from '@css-xp/ppc-sold-to-address-table/src/queries/fetch-ship-to';
import { useFetchBillingProfilesForTransactionAccountQuery } from '@css-xp/ppc-billing-profile-table/src/hooks/use-fetch-billing-profiles-for-tansaction-account-ppc';
import { numberToWords } from '@css-xp/ppc-utils';
import './ship-to-party-sidebar-panel.scss';

type CountryStateObject = { value: string; label: string };
interface FormData {
  soldToName?: string;
  soldToAddress1?: string | null;
  soldToAddress2?: string | null;
  soldToCity?: string | null;
  soldToCountry?: CountryStateObject;
  soldToPostalCode?: string | null;
  soldToState?: CountryStateObject;
  soldToTaxId?: string | null;
  soldToGst?: string | null;
  soldToPst?: string | null;
}

export enum EditShipToPartyContentEnum {
  HEADING = 'HEADING',
  NOTIFICATION_SUCCESS_HEADING = 'NOTIFICATION_SUCCESS_HEADING',
  SUBMIT_BUTTON = 'SUBMIT_BUTTON',
  BILLING_PROFILE_UPDATE_SUCCESS = 'BILLING_PROFILE_UPDATE_SUCCESS',
  ADDRESS_NAME_LABEL = 'ADDRESS_NAME_LABEL',
}

type ContentString = {
  [key in EditShipToPartyContentEnum]?: string;
};

interface Props {
  transactionAccountId: string;
  shipToAddress?: CommerceAddress;
  billToAddress?: CommerceAddress;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  // in case we don't want to fill the "same as billing address" feature
  hideAddFromBillToAddress?: boolean;
  // in case we need to populate few fields by default for adding an address
  // bypassing the logic of opening edit type sidebar everytime ship-to address is present
  isAddType?: boolean;
  // prop to handle the naming convention of the sidebar like sidebar title, notifications sent, etc.
  contentString?: ContentString;
  // To handle the call of any business specific scenario
  onSubmitComplete?: (_responsePayload?: Record<string, any>) => void;
  // handle the cancel button click flow
  onCancelClick?: (_shouldSubmitBeDisabled?: boolean) => void;
  // to handle the failure scenario
  onSubmitFailure?: (_errorMessage?: any) => void;
  // to handle calls before submiting of form
  beforeSubmit?: () => void;
  shouldHideHeading?: boolean;
  shouldHideHeader?: boolean;
  onSubmitCustomerStep?: (
    _customerDetails: CommerceAddress,
    _updateBillToPartyPayload: UpdateBillToAndShipToParties,
    _soldToAddress?: UpdateBillToAndShipToParties[]
  ) => void;
  submitButtonLabel?: string;
  cancelButtonText?: string;
  showPricingEligibility?: boolean;
  FormStages?: string[];
  isProgressIndicatorEnabled?: boolean;
  hideUpdateWarningMessage?: boolean;
  // below props are to be used in edit address flow from billing profile details page in new design
  shouldHideNameField?: boolean;
  billingProfileName?: string;
  billingProfileId?: string;
  showSelectSoldToAddressDropdown?: boolean;
  shouldHideNotificationDescription?: boolean;
  showLinkedBillingProfiles?: boolean;
  // new design specific props end here
  currentProgressIndex?: number;
  shouldAllowMultipleAddresses?: boolean;
  addressess?: CommerceAddress[];
  setAddressess?: (_address: CommerceAddress[]) => void;
  setAddress?: (_address: CommerceAddress) => void;
  isSubmitting?: boolean;
  shouldSubmitBeDisabled?: boolean;
  shouldHideCancelButton?: boolean;
  showDefaultProgressIndicator?: boolean;
  isNameReadOnly?: boolean;
  isCancelDisabled?: boolean;
  submitButtonAppearance?: 'default' | 'primary' | 'subtle' | 'link';
}

const getSuccessNotificationMessage = (isAddShipToAddressFlow: boolean, contentString?: ContentString) => {
  const heading = contentString && contentString[EditShipToPartyContentEnum.NOTIFICATION_SUCCESS_HEADING];
  return heading ? heading : `Sold-to address ${isAddShipToAddressFlow ? 'added' : 'updated'}`;
};

// Handling in case we need to show error related notification as well.
// const getFailureNotificationHeading = (isAddShipToAddressFlow: boolean, contentString?: ContentString) => {
//   const heading = contentString && contentString[EditShipToPartyContentEnum.NOTIFICATION_FAILURE_HEADING];
//   const editFlowString = 'We\'re unable to update sold-to address for the following reason';
//   const addFlowString = 'We\'re unable to create sold-to address for the following reason';
//   return heading ? heading : `${isAddShipToAddressFlow ? addFlowString : editFlowString}`;
// }

const getSidebarHeading = (isAddShipToAddressFlow: boolean, contentString?: ContentString) => {
  const heading = contentString && contentString[EditShipToPartyContentEnum.HEADING];
  return heading ? heading : `${isAddShipToAddressFlow ? 'Add a new' : 'Edit'} sold-to address`;
};

const getSubmitButtonText = (isAddShipToAddressFlow: boolean, contentString?: ContentString) => {
  const submitBtnText = contentString && contentString[EditShipToPartyContentEnum.SUBMIT_BUTTON];
  return submitBtnText ? submitBtnText : `${isAddShipToAddressFlow ? 'Add' : 'Save'}`;
};

export const ShipToPartySidebar = ({
  transactionAccountId,
  shipToAddress,
  billToAddress,
  refetch,
  hideAddFromBillToAddress = false,
  // use it when sold to address details are not available and its in edit flow
  isAddType = false,
  contentString,
  onSubmitComplete,
  onSubmitFailure,
  beforeSubmit,
  onCancelClick,
  shouldHideHeading,
  shouldHideHeader,
  shouldHideNameField,
  billingProfileName,
  billingProfileId,
  showSelectSoldToAddressDropdown,
  shouldHideNotificationDescription,
  showLinkedBillingProfiles,
  onSubmitCustomerStep,
  submitButtonLabel,
  showPricingEligibility,
  FormStages,
  isProgressIndicatorEnabled,
  hideUpdateWarningMessage,
  currentProgressIndex = 0,
  shouldAllowMultipleAddresses,
  cancelButtonText = 'Cancel',
  addressess = [],
  setAddressess,
  setAddress,
  isSubmitting,
  shouldSubmitBeDisabled = true,
  shouldHideCancelButton = false,
  showDefaultProgressIndicator = false,
  isNameReadOnly,
  isCancelDisabled = false,
  submitButtonAppearance,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);
  const isSoldToAddressArchivalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ARCHIVE_SOLD_TO_ADDRESS);
  const { closeSidebar } = useSidebar();
  const [isSubmitDisabled, setSubmitDisabled] = useState<boolean>(shouldSubmitBeDisabled);
  const [soldToName, setSoldToName] = useState<string | null>(null);
  const [selectedCountryCurrency, setSelectedCountryCurrency] = useState<string | null>(shipToAddress?.country || null);
  const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
  const isAddShipToAddressFlow = !shipToAddress || isAddType || !!hideUpdateWarningMessage;

  const {
    data: linkedBillingProfiles,
    error: errorLinkedBillingProfiles,
    loading: loadingLinkedBillingProfiles,
  } = useFetchBillingProfilesForTransactionAccountQuery({
    soldToAddressIds: [shipToAddress?.id as string],
    transactionAccountId: transactionAccountId,
    skipOption: !showLinkedBillingProfiles,
    numberOfBillingProfilesToFetch: 100,
  });

  const ansGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);

  const handleSubmit = async (data: UpdateBillToAndShipToParties) => {
    const promises: Promise<void | string>[] = [updateShipToParty(data)];

    if (data.shipToAddress?.id && data.shipToAddress?.id !== shipToAddress?.id) {
      promises.push(
        updateInvoiceGroup({
          transactionAccountId,
          invoiceGroupId: billingProfileId as string,
          data: {
            shipToPartyId: data.shipToAddress.id,
          },
        })
      );
    }

    return Promise.all(promises);
  };

  const { error: formError, isProcessing, submitForm, complete, fieldErrors } = useFormSubmit<
    UpdateBillToAndShipToParties,
    any
  >({
    submitFormAction: showSelectSoldToAddressDropdown ? handleSubmit : updateShipToParty,
    onComplete: (response) => {
      // Since we get only Id from the api response so we are passing sold to address name from the form data
      onSubmitComplete?.({ ...response, soldToName });
    },
    onFailure: (err) => {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Update unsuccessful')
          .withMessage(failureMessage())
          .withDismissAfter(5000)
          .withId('ship-to-party-failure')
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
      if (onSubmitFailure) onSubmitFailure(err);
    },
    detailedErrorMessage: (err) => {
      const baseMsg = err?.response?.data?.message ?? err?.message;
      const detailedMsg = err?.response?.data?.details?.errors?.details?.detailedDescriptions;
      const errorMessage = detailedMsg ? `${baseMsg}, ${detailedMsg}` : baseMsg;
      if (isEnabled(ENABLE_DESIGN_ITERATION_1) && !errorMessage) {
        return UPDATE_FAILED_MESSAGE;
      }
      return errorMessage;
    },
  });

  const { loading: shipToAddressesLoading, data: shipToAddressesData } = useQuery<
    FetchShipToInPpcQuery,
    FetchShipToInPpcQueryVariables
  >(fetchShipToInPPC, {
    variables: {
      searchParams: {
        companyName: shipToAddress?.name,
        transactionAccountId,
        ...(isSoldToAddressArchivalEnabled && { isActive: true }),
      },
      pageParams: {
        size: CCP_SEARCH_MAX_LIMIT,
      },
    },
    skip: !showSelectSoldToAddressDropdown || !shipToAddress || !transactionAccountId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    client: ansGraphqlClient,
  });

  const [isChecked, setIsChecked] = useState(false);
  //set ship to as bill to if checkbox is selected
  const [newshipToAddress, setNewShipToAddress] = useState(isChecked ? billToAddress : shipToAddress);
  const [countRender, setRenderCount] = useState(0);
  const firstUpdate = React.useRef(true);

  const addAnotherAddress = () => {
    setSubmitDisabled(true);
    setAddressess && setAddressess([...addressess, {}]);
  };

  const removeAddress = (index: number) => {
    setAddressess && setAddressess(addressess.filter((_, i) => i !== index));
  };

  const [showCurrencyWarning, setShowCurrencyWarning] = useState<boolean>(
    isMultiCurrencyChangesEnabled && !isAddShipToAddressFlow ? true : false
  );

  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
  }, [countRender]);

  React.useEffect(() => {
    if (isChecked) {
      setNewShipToAddress(billToAddress);
    } else {
      setNewShipToAddress(shipToAddress);
    }
  }, [isChecked]);

  // const sameAsBillToCheckBoxFeatureFlag = useSelector(
  //   getFeatureFlag(ENABLE_SAME_AS_BILL_TO)
  // );
  const onSameAsBillTo = () => {
    setIsChecked(!isChecked);
    setRenderCount((countRender) => countRender + 1);
  };

  const submitEditBillToPartyDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // After any failure sentnotification is set to true and even after changing the value of form it remains the same hence before Click on Submit value is changed to false
    setNotificationSent(false);
    if (beforeSubmit) beforeSubmit();

    if (shouldAllowMultipleAddresses) {
      const payload = addressess.map((address) => {
        return {
          pricingEligibility: {
            [address.priceEligibility as string]: 'true',
          },
          transactionAccountId,
          shipToAddress: {
            id: newshipToAddress?.id ?? null,
            name: shipToAddress?.name,
            postalAddress: {
              line1: defaultTo(address.addressLine1 || null, ''),
              line2: defaultTo(address.addressLine2 || null, null),
              city: address?.city ?? '',
              state: defaultTo(address?.state || null, null),
              postcode: address?.postcode ?? '',
              country: address?.country ?? '',
            },
            taxId: address?.taxId,
            taxIds: address?.taxIds,
          },
        } as UpdateBillToAndShipToParties;
      });

      onSubmitCustomerStep && onSubmitCustomerStep({}, {} as UpdateBillToAndShipToParties, payload);
      return;
    }

    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });
    const taxIds = getTaxIds(billingData);

    // send the default value of the priceEligibility or send by default commercial
    const pricingEligibilityValue = newshipToAddress?.priceEligibility
      ? {
          [newshipToAddress.priceEligibility]: 'true',
        }
      : {
          commercial: 'true',
        };
    const updateBillToPartyPayload: UpdateBillToAndShipToParties = {
      pricingEligibility: pricingEligibilityValue,
      transactionAccountId,
      shipToAddress: {
        id: newshipToAddress?.id ?? null,
        name: shouldHideNameField
          ? shipToAddress?.name // in new design iteration we are hiding the name field which is required for the update API hence passing the name from props
          : defaultTo(transformedData.soldToName, '').trim(), // remove the leading and trailing spaces as it leads to new address creation for same name
        postalAddress: {
          line1: defaultTo(transformedData.soldToAddress1 || null, ''),
          line2: defaultTo(transformedData.soldToAddress2 || null, null),
          city: transformedData?.soldToCity ?? '',
          state: defaultTo(transformedData?.soldToState?.value || null, null),
          postcode: transformedData?.soldToPostalCode ?? '',
          country: transformedData?.soldToCountry?.value ?? '',
        },
        taxId: transformedData?.soldToTaxId || getFallbackTaxId(taxIds),
        taxIds: taxIds.length ? taxIds : undefined,
      },
    };
    setSoldToName(defaultTo(transformedData.soldToName, null));

    const soldToAddress: CommerceAddress = {
      name: transformedData.soldToName,
      addressLine1: transformedData.soldToAddress1,
      addressLine2: transformedData.soldToAddress2,
      city: transformedData?.soldToCity,
      state: transformedData?.soldToState?.value,
      country: transformedData?.soldToCountry?.value,
      postcode: transformedData?.soldToPostalCode,
      priceEligibility: 'commercial',
      taxId: transformedData?.soldToTaxId || getFallbackTaxId(taxIds),
      taxIds: taxIds.length ? taxIds : undefined,
    };

    if (onSubmitCustomerStep) {
      onSubmitCustomerStep(soldToAddress, updateBillToPartyPayload);
    } else {
      submitForm(updateBillToPartyPayload);
    }
  };

  if (complete && !notificationSent) {
    const billingProfileUpdatedSuccessMessage =
      contentString && contentString[EditShipToPartyContentEnum.BILLING_PROFILE_UPDATE_SUCCESS];
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-shipTo-party-success')
      .withHeader(billingProfileUpdatedSuccessMessage ? billingProfileUpdatedSuccessMessage : 'Update successful')
      .withMessage(
        shouldHideNotificationDescription ? null : getSuccessNotificationMessage(isAddShipToAddressFlow, contentString)
      )
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    if (refetch) {
      refetch();
    }
  }

  const title = useMemo(() => getSidebarHeading(isAddShipToAddressFlow, contentString), [isAddShipToAddressFlow]);
  const submitButtonText = useMemo(() => getSubmitButtonText(isAddShipToAddressFlow, contentString), [
    isAddShipToAddressFlow,
  ]);

  const renderErrorMessage = (formError: string) => {
    let errorMessage: React.ReactNode;
    if (formError === UPDATE_FAILED_MESSAGE) {
      errorMessage = (
        <p>
          <b>{formError}</b>
          <br /> Try again or contact {getSupportUrl()}.
        </p>
      );
    } else {
      errorMessage = <ErrorComponents.ErrorMessageOnInfoPanel message={formError} helpContent={<HelpContent />} />;
    }
    return <InfoPanel type="error" message={errorMessage} />;
  };

  const onFormChange = useMemo(
    () =>
      debounce((getState: () => { invalid: boolean; values: any }, isFetchingData) => {
        if (!shouldAllowMultipleAddresses) {
          setSubmitDisabled(isFetchingData || getState().invalid);

          if (setAddress) {
            const values = getState().values;
            const taxIds = getTaxIds(values);

            const soldToAddress: CommerceAddress = {
              name: values.soldToName,
              addressLine1: values.soldToAddress1,
              addressLine2: values.soldToAddress2,
              city: values?.soldToCity,
              state: values?.soldToState?.value,
              country: values?.soldToCountry?.value,
              postcode: values?.soldToPostalCode,
              priceEligibility: 'commercial',
              taxId: values?.soldToTaxId || getFallbackTaxId(taxIds),
              taxIds: taxIds.length ? taxIds : undefined,
            };

            setAddress(soldToAddress);
          }
        }
      }, 300),
    []
  );

  const onAddressFormChange = useMemo(
    () =>
      debounce((getState: () => { invalid: boolean; values: any }, index: number) => {
        setSubmitDisabled(getState().invalid);

        const values = getState().values;
        const taxIds = getTaxIds(values);

        const soldToAddress: CommerceAddress = {
          name: values.soldToName,
          addressLine1: values.soldToAddress1,
          addressLine2: values.soldToAddress2,
          city: values?.soldToCity,
          state: values?.soldToState?.value,
          country: values?.soldToCountry?.value,
          postcode: values?.soldToPostalCode,
          priceEligibility: 'commercial',
          taxId: values?.soldToTaxId || getFallbackTaxId(taxIds),
          taxIds: taxIds.length ? taxIds : undefined,
        };

        setAddressess &&
          // @ts-ignore
          setAddressess((prev: CommerceAddress[]) => {
            const newAddressess = [...prev];
            newAddressess[index] = { ...soldToAddress };
            return newAddressess;
          });
      }, 300),
    []
  );

  const onCancel = () => {
    closeSidebar();
    onCancelClick && onCancelClick(isSubmitDisabled);
  };

  const onAddressSelectionChange = (selectedOption: any) => {
    const allAddresses = shipToAddressesData?.shipToParty?.nodes;
    const selectedAddress = allAddresses?.find((address) => address?.id === selectedOption.value);
    setNewShipToAddress(selectedAddress as CommerceAddress);
  };

  const onCountryChange = (selectedCountry?: IsoSelectItem) => {
    const countryCode = selectedCountry?.value;
    if (isMultiCurrencyChangesEnabled && countryCode) {
      const currency = getCurrencyByCountry(countryCode);
      setSelectedCountryCurrency(currency);
      setShowCurrencyWarning(currency !== Currency.USD);
    } else {
      setShowCurrencyWarning(false);
    }
  };

  const renderAddressForm = (index = 0, getState: any, setFieldValue: any, onFormChangeFunction = onFormChange) => {
    const address = addressess[index] || {};

    const extraProps = shouldAllowMultipleAddresses ? { key: index } : {};
    return (
      <>
        <AddressFormSectionV2
          {...newshipToAddress}
          {...address}
          {...extraProps}
          isChecked={isChecked}
          firstUpdate={firstUpdate.current}
          isEditable={true}
          addressNameLabel={contentString && contentString[EditShipToPartyContentEnum.ADDRESS_NAME_LABEL]}
          addressName="Sold-to"
          setFieldValue={setFieldValue}
          isRequired={true}
          taxIdAddressPrefix={'Sold-to'}
          // Name read only when add to sold-to address flow or update address only flow in new design
          isNameReadOnly={isAddType || isNameReadOnly}
          fieldErrors={fieldErrors}
          setOnFetchingData={onFormChangeFunction.bind(null, getState, index)}
          onCountryChange={(selectedCountry?: IsoSelectItem) => {
            isMultiCurrencyChangesEnabled && onCountryChange(selectedCountry);
            onFormChangeFunction.bind(null, getState, index);
          }}
          onStateChange={onFormChangeFunction.bind(null, getState, index)}
          hideNameField={shouldHideNameField}
          countryMessage={isMultiCurrencyChangesEnabled ? currencyWarningMsg : null}
          isCountryReadOnly={isMultiCurrencyChangesEnabled && !isAddShipToAddressFlow}
          showPricingEligibilityField={showPricingEligibility}
          pricingEligibilityInfoMsg={pricingEligibilityInfoMsg}
          shouldShowCurrencyInfo={isMultiCurrencyChangesEnabled && isDesignIteration1Enabled}
        />
      </>
    );
  };

  const currencyWarningMsg = useMemo(() => {
    if (showCurrencyWarning && selectedCountryCurrency && !isRedesignFastFollowEnabled) {
      return (
        <div className="currency-warning">
          <EditorInfoIcon label="info" size="medium" primaryColor={token('color.icon.discovery', P200)} />
          <div className="warning-message">
            <Text color="color.text.subtlest">
              {isAddShipToAddressFlow ? (
                `Currency will be set to ${selectedCountryCurrency} for this sold to address.`
              ) : (
                <>
                  Changing the country will impact the linked currency. Contact{' '}
                  <a href={getPartnerSupportUrl()} target="_blank">
                    Partner Service Desk
                  </a>{' '}
                  to change your country.
                </>
              )}
            </Text>
          </div>
        </div>
      );
    }
    return null;
  }, [showCurrencyWarning, isAddShipToAddressFlow, selectedCountryCurrency, isRedesignFastFollowEnabled]);

  const pricingEligibilityInfoMsg = useMemo(() => {
    if (showPricingEligibility) {
      return (
        <div className="currency-warning">
          <EditorInfoIcon label="info" size="medium" primaryColor={token('color.icon.discovery', P200)} />
          <div className="warning-message">
            <Text color="color.text.subtlest">
              <>
                If you want to modify the pricing eligibilty, please contact{' '}
                <a href={getPartnerSupportUrl()} target="_blank">
                  Partner Service Desk
                </a>{' '}
              </>
            </Text>
          </div>
        </div>
      );
    }
    return null;
  }, [showPricingEligibility]);

  return (
    <div className="pane-sidebar-content" data-testid="ship-to-party-sidebar">
      <Form onSubmit={submitEditBillToPartyDetails}>
        {({ formProps, submitting, setFieldValue, getState }) => (
          <form {...formProps} onChange={onFormChange.bind(null, getState, null)}>
            {!shouldHideHeader && (
              <>
                {!shouldHideHeading && <FormHeader title={title} />}
                {shouldHideHeading && (
                  <FormHeader>
                    <Box paddingBlockEnd="space.200" testId="required-fields-message">
                      <p aria-hidden="true" className="required-field">
                        Required fields are marked with an asterisk <RequiredAsterisk />
                      </p>
                    </Box>
                  </FormHeader>
                )}
              </>
            )}
            {!isAddShipToAddressFlow ? (
              <>
                <InfoPanel
                  type="warning"
                  message={
                    'Updating the sold-to address can impact the amount of tax applied on upcoming payments. All subscriptions that use the current sold-to address will also get updated.'
                  }
                />
                <div className="mt-8" data-testId="inflight-invoice-banner">
                  <InflightInvoiceBanner
                    transactionAccountId={transactionAccountId}
                    message={DEFAULT_INFLIGHT_BANNER_MESSAGE}
                  />
                </div>
              </>
            ) : null}

            {formError && (
              <FormSection>
                <div className="form-submit-error-container">{formError && renderErrorMessage(formError)}</div>
              </FormSection>
            )}

            {showLinkedBillingProfiles && (
              <AssociatedBillingProfiles
                isLoadingBillingProfiles={loadingLinkedBillingProfiles}
                billingProfiles={linkedBillingProfiles?.transactionAccount?.billingProfiles?.nodes as InvoiceGroup[]}
                txaId={transactionAccountId}
                error={errorLinkedBillingProfiles}
              />
            )}

            <FormSection>
              {billingProfileName && (
                <Tooltip content={billingProfileName} position="bottom-start" tag="span">
                  <Field
                    testId="billing-profile-name-field"
                    name="billing-profile-name"
                    isDisabled
                    defaultValue={billingProfileName}
                    label="Billing profile name"
                  >
                    {({ fieldProps }) => <TextField {...fieldProps} />}
                  </Field>
                </Tooltip>
              )}

              {showSelectSoldToAddressDropdown && (
                <Box paddingBlockStart="space.100" testId="select-ship-to-address">
                  <Label htmlFor="selectShipTo">Switch to another address</Label>
                  <Select
                    onChange={onAddressSelectionChange}
                    options={
                      shipToAddressesData?.shipToParty?.nodes?.map((node) => ({
                        label: node && formatAddressLabel(node as CommerceAddress, true), // don't show the name in the dropdown
                        value: node?.id,
                      })) || []
                    }
                    isLoading={shipToAddressesLoading}
                    isDisabled={shipToAddressesLoading}
                    id="selectShipTo"
                    aria-label="Switch to another address"
                    defaultValue={{
                      label: formatAddressLabel(newshipToAddress as CommerceAddress, true),
                      value: newshipToAddress?.id,
                    }}
                  />
                </Box>
              )}

              {!shipToAddress && !hideAddFromBillToAddress && (
                <Checkbox
                  isChecked={isChecked}
                  onChange={onSameAsBillTo}
                  label="Same as billing address"
                  value="Controlled Checkbox"
                  name="controlled-checkbox"
                />
              )}
              <div>
                {shouldAllowMultipleAddresses ? (
                  <>
                    {addressess.map((_: any, index) => (
                      <Box key={index}>
                        {index > 0 && (
                          <Box paddingBlockStart="space.100">
                            <Flex direction="column" gap="space.250">
                              <Divider />
                              <Flex justifyContent="space-between" alignItems="center">
                                <Text>{startCase(numberToWords(index + 1))} address</Text>
                                <Button appearance="subtle" onClick={() => removeAddress(index)}>
                                  <TrashIcon label="delete permissions" size="small" />
                                </Button>
                              </Flex>
                            </Flex>
                          </Box>
                        )}
                        {/* @ts-ignore */}
                        <Form>
                          {({ formProps, setFieldValue, getState }) => (
                            <form {...formProps} onChange={onAddressFormChange.bind(null, getState, index)}>
                              {renderAddressForm(index, getState, setFieldValue, onAddressFormChange)}
                            </form>
                          )}
                        </Form>
                      </Box>
                    ))}
                    <Box paddingBlockStart="space.250">
                      <Button
                        id="add-another-address"
                        appearance="subtle"
                        iconBefore={<AddIcon label="Add icon" size="small" />}
                        onClick={addAnotherAddress}
                        isDisabled={isSubmitDisabled}
                      >
                        Another address
                      </Button>
                    </Box>
                  </>
                ) : (
                  renderAddressForm(0, getState, setFieldValue)
                )}
              </div>
            </FormSection>

            {isProgressIndicatorEnabled ? (
              <div className="form-footer" data-testId="progress-indicator-form-footer">
                <Button
                  appearance={isEnabled(ENABLE_DESIGN_ITERATION_1) ? 'default' : 'subtle'}
                  onClick={onCancel}
                  isDisabled={isCancelDisabled}
                >
                  {cancelButtonText}
                </Button>
                {FormStages && (
                  <ProgressIndicator
                    selectedIndex={currentProgressIndex}
                    values={FormStages}
                    appearance={showDefaultProgressIndicator ? 'default' : 'primary'}
                    size={showDefaultProgressIndicator ? 'default' : 'large'}
                  />
                )}
                <LoadingButton
                  appearance={submitButtonAppearance ? submitButtonAppearance : 'primary'}
                  type="submit"
                  isLoading={submitting || isProcessing || isSubmitting}
                  isDisabled={isSubmitDisabled}
                  testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                >
                  {submitButtonLabel ? submitButtonLabel : submitButtonText}
                </LoadingButton>
              </div>
            ) : (
              <FormFooter align="end">
                <ButtonGroup>
                  {!shouldHideCancelButton && (
                    <Button appearance={isEnabled(ENABLE_DESIGN_ITERATION_1) ? 'default' : 'subtle'} onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={submitting || isProcessing}
                    isDisabled={isSubmitDisabled}
                    testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                  >
                    {submitButtonLabel ? submitButtonLabel : submitButtonText}
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};
